/** True when the event target is a field where typing shortcuts should not run. */
export function shouldIgnoreKeyboardShortcut(event: KeyboardEvent): boolean {
  const pathTarget = event.composedPath?.()[0];
  const target = pathTarget instanceof Node ? pathTarget : event.target;
  if (!(target instanceof HTMLElement)) return false;

  return Boolean(
    target.isContentEditable ||
      target.closest("input, textarea, select, [contenteditable='true']"),
  );
}

export function isApplePlatform(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/** Tooltip label for the platform's primary modifier (Command on Mac, Ctrl elsewhere). */
export function formatPrimaryShortcut(key: string): string {
  if (!key) return "";
  const letter = key.toUpperCase();
  return isApplePlatform() ? `Cmd+${letter}` : `Ctrl+${letter}`;
}

/** Tooltip label for Ctrl+Alt+key (Option on Mac). */
export function formatCtrlAltShortcut(key: string): string {
  const letter = key.toUpperCase();
  return isApplePlatform() ? `Ctrl+⌥+${letter}` : `Ctrl+Alt+${letter}`;
}

function hasCtrlAltModifiers(event: KeyboardEvent): boolean {
  if (event.metaKey) return false;
  if (event.getModifierState("AltGraph")) return true;

  const ctrl = event.ctrlKey || event.getModifierState("Control");
  const alt = event.altKey || event.getModifierState("Alt");
  return Boolean(ctrl && alt);
}

function letterKeyCode(letter: string): string | undefined {
  const ch = letter.toLowerCase();
  if (ch.length !== 1 || ch < "a" || ch > "z") return undefined;
  return `Key${ch.toUpperCase()}`;
}

/**
 * Match Ctrl+Alt+letter (Control+Option on Mac).
 *
 * Uses `event.code` because on Windows Ctrl+Alt is often AltGr and `event.key`
 * is not the base letter; on Mac Option also remaps `event.key` to symbols.
 */
export function isCtrlAltLetter(event: KeyboardEvent, letter: string): boolean {
  if (event.shiftKey) return false;
  if (!hasCtrlAltModifiers(event)) return false;

  const normalized = letter.toLowerCase();
  const expectedCode = letterKeyCode(normalized);
  if (expectedCode && event.code === expectedCode) return true;

  const key = event.key;
  return key.length === 1 && key.toLowerCase() === normalized;
}

/**
 * Match the platform's primary modifier plus a letter: Command on Apple devices,
 * Ctrl elsewhere. Extra modifiers are rejected so an unrelated browser or app
 * chord is never claimed accidentally.
 */
export function isPrimaryModifierLetter(event: KeyboardEvent, letter: string): boolean {
  if (event.altKey || event.shiftKey) return false;

  const modifiersMatch = isApplePlatform()
    ? event.metaKey && !event.ctrlKey
    : event.ctrlKey && !event.metaKey;
  if (!modifiersMatch) return false;

  const normalized = letter.toLowerCase();
  const expectedCode = letterKeyCode(normalized);
  if (expectedCode && event.code === expectedCode) return true;

  const key = event.key;
  return key.length === 1 && key.toLowerCase() === normalized;
}
